import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { baseTileLayer, directLine, markerIcon, type MarkerKind } from '../lib/leaflet';
import type { Coords } from '../lib/types';

interface Props {
  value: Coords | null;
  onChange: (coords: Coords) => void;
  kind?: MarkerKind;
  tall?: boolean;
  /** Zweiter Punkt nur zur Orientierung - z.B. der Startpunkt, während der
   *  Abholpunkt gesetzt wird. Nicht antipp- oder ziehbar. */
  secondary?: Coords | null;
  secondaryKind?: MarkerKind;
}

/**
 * Karte zum Setzen eines Punktes: antippen oder den Marker ziehen.
 */
export function MapPicker({ value, onChange, kind = 'pickup', tall = false, secondary = null, secondaryKind = 'origin' }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const secondaryMarker = useRef<L.Marker | null>(null);
  const connector = useRef<L.Polyline | null>(null);
  // Über ein Ref, damit ein neuer Callback nicht die Karte neu aufbaut.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!container.current || map.current) return;

    const start: L.LatLngExpression = value ? [value.lat, value.lng] : [51.1657, 10.4515];
    const instance = L.map(container.current, { zoomControl: false, attributionControl: true })
      .setView(start, value ? 16 : 5);
    baseTileLayer().addTo(instance);
    // Keine Zoomknöpfe: sie verdecken auf der kleinen Karte zu viel, und
    // auf dem Handy zoomt man mit zwei Fingern.

    instance.on('click', (event: L.LeafletMouseEvent) => {
      onChangeRef.current({ lat: event.latlng.lat, lng: event.latlng.lng });
    });

    map.current = instance;
    // Ionic blendet Seiten ein - die Karte muss ihre Größe danach neu messen.
    const timer = window.setTimeout(() => instance.invalidateSize(), 250);

    return () => {
      window.clearTimeout(timer);
      instance.remove();
      map.current = null;
      marker.current = null;
      secondaryMarker.current = null;
      connector.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const instance = map.current;
    if (!instance || !value) return;
    const position: L.LatLngExpression = [value.lat, value.lng];

    if (!marker.current) {
      marker.current = L.marker(position, { icon: markerIcon(kind), draggable: true })
        .addTo(instance)
        .on('dragend', (event) => {
          const { lat, lng } = (event.target as L.Marker).getLatLng();
          onChangeRef.current({ lat, lng });
        });
      instance.setView(position, 16);
    } else {
      marker.current.setLatLng(position);
      marker.current.setIcon(markerIcon(kind));
      if (!instance.getBounds().contains(position)) instance.panTo(position);
    }
  }, [value, kind]);

  // Zweiter Punkt nur als Orientierung - eigener Effekt, damit er das
  // Zentrieren auf `value` oben nicht stört.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;

    if (!secondary) {
      secondaryMarker.current?.remove();
      secondaryMarker.current = null;
      connector.current?.remove();
      connector.current = null;
      return;
    }

    const position: L.LatLngExpression = [secondary.lat, secondary.lng];
    if (!secondaryMarker.current) {
      secondaryMarker.current = L.marker(position, { icon: markerIcon(secondaryKind), interactive: false }).addTo(instance);
    } else {
      secondaryMarker.current.setLatLng(position);
      secondaryMarker.current.setIcon(markerIcon(secondaryKind));
    }

    if (value) {
      const line = directLine([value.lat, value.lng], position);
      if (!connector.current) {
        connector.current = line.addTo(instance);
      } else {
        connector.current.setLatLngs([[value.lat, value.lng], position]);
      }
    }
  }, [secondary, secondaryKind, value]);

  return <div ref={container} className={tall ? 'r2f-map r2f-map--tall' : 'r2f-map'} />;
}
